(() => {
  const infoDrawerMap = new WeakMap();
  const sizeGuideDrawerMap = new WeakMap();
  const infoDrawerByNode = new WeakMap();
  const sizeGuideDrawerByNode = new WeakMap();
  const portalDrawerToBody = (drawer) => {
    if (!drawer || drawer.parentElement === document.body) return;
    document.body.appendChild(drawer);
  };
  const scrollLock = {
    locked: false,
    scrollY: 0,
  };

  const lockScroll = () => {
    if (scrollLock.locked) return;
    scrollLock.locked = true;
    scrollLock.scrollY = window.scrollY || window.pageYOffset || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollLock.scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
  };

  const unlockScroll = () => {
    if (!scrollLock.locked) return;
    scrollLock.locked = false;
    const y = scrollLock.scrollY;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    window.scrollTo(0, y);
  };

  class ProductInfoDrawer {
    constructor(root) {
      this.root = root;
      this.drawer = root.querySelector('.product-info-drawer');
      portalDrawerToBody(this.drawer);
      this.tabs = this.drawer
        ? this.drawer.querySelectorAll('.product-info-drawer__tab[data-drawer-panel]')
        : [];
      this.panels = this.drawer
        ? this.drawer.querySelectorAll('.product-info-drawer__content[data-panel]')
        : [];
      this.scrollContainer = this.drawer ? this.drawer.querySelector('.product-info-drawer__body') : null;
      this.body = document.body;
      this.activePanel = 'shipping';
      this.closeTimer = null;
      this.motionMs = 340;
    }

    init() {
      if (!this.drawer) return;

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.drawer.hasAttribute('hidden')) this.close();
      });
    }

    open(panelName) {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.drawer.removeAttribute('hidden');
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
      });
      this.body.classList.add('product-info-drawer-open');
      lockScroll();
      this.setPanel(panelName);
      if (this.scrollContainer) this.scrollContainer.scrollTop = 0;
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.body.classList.remove('product-info-drawer-open');
      unlockScroll();
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.drawer.setAttribute('hidden', '');
      }, this.motionMs);
    }

    setPanel(panelName) {
      const panelChanged = this.activePanel !== panelName;
      this.activePanel = panelName;
      this.panels.forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.panel === this.activePanel);
      });
      this.tabs.forEach((tab) => {
        const isActive = tab.dataset.drawerPanel === this.activePanel;
        tab.classList.toggle('is-active', isActive);
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      if (panelChanged && this.scrollContainer) this.scrollContainer.scrollTop = 0;
    }

  }

  class SizeGuideDrawer {
    constructor(root) {
      this.root = root;
      this.drawer = root.querySelector('.size-guide-drawer');
      portalDrawerToBody(this.drawer);
      this.body = document.body;
      this.closeTimer = null;
      this.motionMs = 340;
    }

    init() {
      if (!this.drawer) return;

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && !this.drawer.hasAttribute('hidden')) this.close();
      });
    }

    open(panelName = 'border') {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }
      this.drawer.removeAttribute('hidden');
      requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
      });
      this.body.classList.add('product-info-drawer-open');
      lockScroll();
      this.setPanel(panelName);
    }

    close() {
      this.drawer.classList.remove('is-open');
      this.body.classList.remove('product-info-drawer-open');
      unlockScroll();
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      this.closeTimer = window.setTimeout(() => {
        this.drawer.setAttribute('hidden', '');
      }, this.motionMs);
    }

    setPanel(panelName) {
      const target = panelName === 'full-bleed' ? 'full-bleed' : 'border';
      this.drawer.querySelectorAll('[data-size-guide-panel]').forEach((panel) => {
        panel.classList.toggle('is-active', panel.dataset.sizeGuidePanel === target);
      });
      this.drawer.querySelectorAll('[data-size-guide-mode]').forEach((button) => {
        const isActive = button.dataset.sizeGuideMode === target;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  const getInfoDrawerForElement = (el) => {
    const root = el.closest('product-info');
    if (!root) {
      const drawerNode = el.closest('.product-info-drawer');
      if (drawerNode && infoDrawerByNode.has(drawerNode)) return infoDrawerByNode.get(drawerNode);
      return null;
    }
    if (infoDrawerMap.has(root)) return infoDrawerMap.get(root);
    const drawer = new ProductInfoDrawer(root);
    drawer.init();
    infoDrawerMap.set(root, drawer);
    if (drawer.drawer) infoDrawerByNode.set(drawer.drawer, drawer);
    return drawer;
  };

  const getSizeGuideDrawerForElement = (el) => {
    const root = el.closest('product-info');
    if (!root) {
      const drawerNode = el.closest('.size-guide-drawer');
      if (drawerNode && sizeGuideDrawerByNode.has(drawerNode)) return sizeGuideDrawerByNode.get(drawerNode);
      return null;
    }
    if (sizeGuideDrawerMap.has(root)) return sizeGuideDrawerMap.get(root);
    const drawer = new SizeGuideDrawer(root);
    drawer.init();
    sizeGuideDrawerMap.set(root, drawer);
    if (drawer.drawer) sizeGuideDrawerByNode.set(drawer.drawer, drawer);
    return drawer;
  };

  const initAll = () => {
    document.querySelectorAll('product-info').forEach((root) => {
      if (!infoDrawerMap.has(root)) {
        const infoDrawer = new ProductInfoDrawer(root);
        infoDrawer.init();
        infoDrawerMap.set(root, infoDrawer);
        if (infoDrawer.drawer) infoDrawerByNode.set(infoDrawer.drawer, infoDrawer);
      }

      if (!sizeGuideDrawerMap.has(root)) {
        const sizeGuideDrawer = new SizeGuideDrawer(root);
        sizeGuideDrawer.init();
        sizeGuideDrawerMap.set(root, sizeGuideDrawer);
        if (sizeGuideDrawer.drawer) sizeGuideDrawerByNode.set(sizeGuideDrawer.drawer, sizeGuideDrawer);
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('click', (event) => {
    const sizeGuideOpenButton = event.target.closest('[data-size-guide-open]');
    if (sizeGuideOpenButton) {
      event.preventDefault();
      const sizeGuideDrawer = getSizeGuideDrawerForElement(sizeGuideOpenButton);
      if (!sizeGuideDrawer) return;
      sizeGuideDrawer.open('border');
      return;
    }

    const sizeGuideModeButton = event.target.closest('[data-size-guide-mode]');
    if (sizeGuideModeButton) {
      const sizeGuideDrawer = getSizeGuideDrawerForElement(sizeGuideModeButton);
      if (!sizeGuideDrawer) return;
      sizeGuideDrawer.setPanel(sizeGuideModeButton.dataset.sizeGuideMode);
      return;
    }

    const sizeGuideCloseButton = event.target.closest('[data-size-guide-close]');
    if (sizeGuideCloseButton) {
      const sizeGuideDrawer = getSizeGuideDrawerForElement(sizeGuideCloseButton);
      if (!sizeGuideDrawer) return;
      sizeGuideDrawer.close();
      return;
    }

    const openButton = event.target.closest('[data-drawer-panel]');
    if (openButton) {
      const infoDrawer = getInfoDrawerForElement(openButton);
      if (!infoDrawer) return;
      const panelName = openButton.dataset.drawerPanel || 'shipping';
      const isTab = openButton.classList.contains('product-info-drawer__tab');
      if (isTab && !infoDrawer.drawer.hasAttribute('hidden')) {
        infoDrawer.setPanel(panelName);
      } else {
        infoDrawer.open(panelName);
      }
      return;
    }

    const closeButton = event.target.closest('[data-drawer-close]');
    if (closeButton) {
      const infoDrawer = getInfoDrawerForElement(closeButton);
      if (!infoDrawer) return;
      infoDrawer.close();
    }
  });

  document.addEventListener('shopify:section:load', initAll);
})();
